import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const BulkShareModal = ({ isOpen, onClose, onBulkShare, selectedShares }) => {
  const [bulkAction, setBulkAction] = useState('');
  const [bulkSettings, setBulkSettings] = useState({
    expiresAt: '',
    accessLevel: '',
    usageLimit: '',
    requireApproval: false
  });

  const bulkActions = [
    { value: 'extend', label: 'Extend Expiration', description: 'Extend access time for selected shares' },
    { value: 'revoke', label: 'Revoke Access', description: 'Immediately revoke access for selected shares' },
    { value: 'modify', label: 'Modify Permissions', description: 'Change access level or settings' },
    { value: 'notify', label: 'Send Notification', description: 'Send message to recipients' }
  ];

  const accessLevels = [
    { value: 'viewer', label: 'Viewer' },
    { value: 'contributor', label: 'Contributor' },
    { value: 'manager', label: 'Manager' }
  ];

  const handleBulkAction = () => {
    const actionData = {
      action: bulkAction,
      shareIds: selectedShares?.map(share => share?.id),
      settings: bulkSettings
    };
    onBulkShare(actionData);
    onClose();
    setBulkAction('');
    setBulkSettings({
      expiresAt: '',
      accessLevel: '',
      usageLimit: '',
      requireApproval: false
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-300 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Bulk Share Actions</h2>
            <p className="text-sm text-muted-foreground">
              {selectedShares?.length} share{selectedShares?.length !== 1 ? 's' : ''} selected
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Selected Shares Preview */}
          <div>
            <h3 className="font-medium text-foreground mb-3">Selected Shares</h3>
            <div className="max-h-32 overflow-y-auto space-y-2">
              {selectedShares?.map((share) => (
                <div key={share?.id} className="flex items-center space-x-3 p-2 bg-muted rounded">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="User" size={16} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{share?.recipientName}</p>
                    <p className="text-xs text-muted-foreground">{share?.recipientEmail}</p>
                  </div>
                  <span className="text-xs text-muted-foreground capitalize">{share?.status}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Action Selection */}
          <div>
            <Select
              label="Bulk Action"
              description="Choose the action to apply to all selected shares"
              options={bulkActions}
              value={bulkAction}
              onChange={setBulkAction}
              placeholder="Select an action..."
            />
          </div>

          {/* Action-specific Settings */}
          {bulkAction === 'extend' && (
            <div className="space-y-4">
              <h4 className="font-medium text-foreground">Extension Settings</h4>
              <input
                type="datetime-local"
                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground"
                value={bulkSettings?.expiresAt}
                onChange={(e) => setBulkSettings({ ...bulkSettings, expiresAt: e?.target?.value })}
              />
            </div>
          )}

          {bulkAction === 'modify' && (
            <div className="space-y-4">
              <h4 className="font-medium text-foreground">Permission Settings</h4>
              <Select
                label="New Access Level"
                options={accessLevels}
                value={bulkSettings?.accessLevel}
                onChange={(value) => setBulkSettings({ ...bulkSettings, accessLevel: value })}
              />
              <input
                type="number"
                placeholder="Usage limit (optional)"
                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground"
                value={bulkSettings?.usageLimit}
                onChange={(e) => setBulkSettings({ ...bulkSettings, usageLimit: e?.target?.value })}
              />
              <Checkbox
                label="Require approval for sensitive actions"
                checked={bulkSettings?.requireApproval}
                onChange={(e) => setBulkSettings({ ...bulkSettings, requireApproval: e?.target?.checked })}
              />
            </div>
          )}

          {bulkAction === 'revoke' && (
            <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="AlertTriangle" size={16} className="text-error" />
                <h4 className="font-medium text-error">Warning</h4>
              </div>
              <p className="text-sm text-error">
                This will immediately revoke access for all selected shares. This action cannot be undone.
              </p>
            </div>
          )}

          {bulkAction === 'notify' && (
            <div className="space-y-4">
              <h4 className="font-medium text-foreground">Notification Settings</h4>
              <textarea
                placeholder="Enter message to send to recipients..."
                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground h-24 resize-none"
                rows={3}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkAction}
            disabled={!bulkAction}
            variant={bulkAction === 'revoke' ? 'destructive' : 'default'}
          >
            {bulkAction === 'revoke' && <Icon name="AlertTriangle" size={16} className="mr-2" />}
            Apply to {selectedShares?.length} Share{selectedShares?.length !== 1 ? 's' : ''}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkShareModal;